/**
 * Data and methods: one card on Settings, six folded rows.
 *
 * Sep 9 2026, twice. In the afternoon Thomas moved this off the dashboard
 * ("it belongs in settings, not on the page a VP reads weekly"). In the
 * evening he looked at it on Settings and saw the same problem the roster
 * section had: five cards of running text in three type sizes. So it is now
 * ONE card, and each block is a row that opens on demand, in a single type
 * style: the row label at the UI size, its one-line hint at the meta size,
 * the body at body size. Nothing is lost; nothing is on the page until it is
 * asked for.
 *
 * The sixth row (Sep 12 2026) is the capability manifest: what P3 does today,
 * what it plans, and what it does not offer. It renders the Worker's own
 * manifest (src/worker/lib/capabilities.ts), never a copy typed here, so the
 * list a customer reads is the one the API serves.
 *
 * Nobody opens a dashboard for this. The person who reads it is the
 * institutional research director or the data governance officer whose
 * signature the contract needs, and for them it is the most important text
 * in the product. Three things make it work, and softening any of them would
 * defeat it:
 *
 *   The reporting threshold is framed as a shared standard rather than an
 *   apology. Ten is what state education agencies use.
 *
 *   "What P3 does not verify" is a list, in plain words, that says what P3
 *   holds instead. The earlier title ("does not measure") read as if students'
 *   graduation, employer and applications were invisible to P3, and Thomas
 *   pushed back on it twice: students report them, the app records them, and
 *   the distinction that survives is verification.
 *
 *   Every panel that is absent says why it is absent.
 */

import type { ReactNode } from "react";

import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
} from "~/app/components/primitives";
import {
  DIRECTIONAL_DENOMINATOR,
  INTERVAL_DENOMINATOR,
  asOfStamp,
} from "~/app/lib/format";
import {
  ABSENT_PANELS,
  AS_OF,
  KNOWN_LIMITS,
  NOT_VERIFIED,
  PLATFORM,
  SOURCE_TABLE,
  type AbsentPanel,
  type CohortView,
} from "~/app/lib/demo-data";
import { capabilityColumnsFrom, type CapabilityManifestShape } from "~/app/lib/pricing";
import { K } from "~/lib/suppression";

/** The manifest as the page holds it: on the session for a signed-in
 *  institution, read from the public route for the sample. */
export type CapabilitiesState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly manifest: CapabilityManifestShape }
  | { readonly kind: "unavailable" };

/** A row that opens. Native details/summary, so it needs no script to work
 *  and the keyboard gets it for free; the caret is the only decoration. */
function Fold({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <details className="e-fold">
      <summary>
        <span className="e-fold-label">{label}</span>
        <span className="e-fold-hint">{hint}</span>
        <svg className="e-fold-caret" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M3.5 6l4.5 4.5L12.5 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </summary>
      <div className="e-fold-body">{children}</div>
    </details>
  );
}

export function MethodsPanel({
  view,
  capabilities,
}: {
  view: CohortView;
  capabilities: CapabilitiesState;
}) {
  return (
    <div className="e-grid" id="data-and-methods">
      <Card span="full">
        <CardHeader>
          <CardTitle>Data and methods</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="e-folds">
            <Fold
              label="Where each number comes from"
              hint={`${SOURCE_TABLE.length} families of figures, one source each. ${asOfStamp(AS_OF)}`}
            >
              <DataTable
                caption="Sources for every figure on this dashboard"
                rows={SOURCE_TABLE}
                rowKey={(row) => row.what}
                cap={12}
                moreNoun="source"
                columns={[
                  { key: "what", header: "What", cell: (row) => row.what, text: (row) => row.what },
                  { key: "source", header: "Source", cell: (row) => row.source, text: (row) => row.source },
                  {
                    key: "cadence",
                    header: "Refreshed",
                    cell: (row) => row.cadence,
                    text: (row) => row.cadence,
                    secondary: true,
                  },
                  {
                    key: "precision",
                    header: "Exact or estimated",
                    cell: (row) => row.precision,
                    text: (row) => row.precision,
                  },
                ]}
              />
              <p className="e-note">
                All figures come from one snapshot, recomputed hourly.
              </p>
            </Fold>

            <Fold
              label="The reporting threshold"
              hint={`No figure below ${K} students. The threshold state education reporting uses.`}
            >
              <p>
                P3 does not report any figure that represents fewer than {K}{" "}
                individuals. Where hiding one figure would let you work it out by
                subtracting the others, the next smallest figure is hidden too.
              </p>
              <p>Suppressed figures roll into a remainder line, never a blank or a zero.</p>
              <p>
                This is a written commitment in the Data Sharing Addendum you
                accepted, Section V. It is applied before a figure leaves P3's
                servers, so a suppressed value never reaches this browser.
              </p>
            </Fold>

            <Fold
              label="What is not here, and why"
              hint={`${ABSENT_PANELS.length} panels you will not find, each with its reason.`}
            >
              <ul>
                {ABSENT_PANELS.map((panel: AbsentPanel) => (
                  <li key={panel.title}>
                    <strong>{panel.title}</strong>{" "}
                    <Chip tone={panel.status === "Cut" ? "neutral" : "warn"}>{panel.status}</Chip>
                    <br />
                    {panel.reason}
                  </li>
                ))}
              </ul>
              <p className="e-note">
                Cut means P3 has no field for it. Not yet means the group is still
                too small to publish.
              </p>
            </Fold>

            <Fold
              label="What P3 does not verify"
              hint="Student reported figures are labeled that way, everywhere they appear."
            >
              <ul>
                {NOT_VERIFIED.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Fold>

            <Fold
              label="Known limits"
              hint="The asymmetries a partner asks about the first time a figure moves."
            >
              <ul>
                {KNOWN_LIMITS.map((limit) => (
                  <li key={limit}>{limit}</li>
                ))}
              </ul>
              <p>
                Counts come from P3's student records, read whole rather than sampled,
                and grouped by the institution on each record. Suppression is applied
                before serialization, so a withheld value is never sent to the browser.
              </p>
              <p>
                Rates carry their numerator and denominator. Below a denominator of{" "}
                {INTERVAL_DENOMINATOR} the interval is printed alongside; below{" "}
                {DIRECTIONAL_DENOMINATOR} the figure carries a Directional chip.
              </p>
              <p>
                The comparison group for "All P3 students" is students who have not
                named an institution. {PLATFORM.exclusion} It is a comparison between
                groups, never an effect.
              </p>
              <p>Weekly series are anchored to Sunday and exclude the current partial week.</p>
            </Fold>

            <Fold
              label="What P3 does, and does not do"
              hint={
                capabilities.kind === "ready"
                  ? `${capabilities.manifest.shipped.length} working today, ${capabilities.manifest.plannedFrom.length} planned, ${capabilities.manifest.notOffered.length} not offered.`
                  : "What works today, what is planned, and what P3 does not offer."
              }
            >
              {capabilities.kind === "ready" ? (
                capabilityColumnsFrom(capabilities.manifest).map((column) => (
                  <div className="e-fold-group" key={column.id}>
                    <p>
                      <strong>{column.eyebrow}</strong>
                    </p>
                    <ul>
                      {column.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <p>
                  {capabilities.kind === "loading"
                    ? "Loading the list."
                    : "The list could not be loaded. Reload the page to try again."}
                </p>
              )}
            </Fold>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
